/**
 * FamilySelectScreen — 亲友团选择界面
 *
 * 每方选择1-2位亲友，可自定义性格和职业，也可使用默认值。
 */
import React, { useState, useCallback } from 'react';
import { ChevronRight, Plus, X, Shuffle } from 'lucide-react';
import type { FamilyMemberInput, FamilyRole } from '@shared/types/family';
import type { Character } from '@shared/types/character';
import { FAMILY_ROLE_DEFAULTS } from '@shared/constants/familyDefaults';

interface FamilySelectScreenProps {
  protagonistA: Character;
  protagonistB: Character;
  onComplete: (familyA: FamilyMemberInput[], familyB: FamilyMemberInput[]) => void;
  isGenerating: boolean;
}

interface FamilySideProps {
  label: string;
  protagonist: Character;
  members: FamilyMemberInput[];
  onChange: (members: FamilyMemberInput[]) => void;
}

const AVAILABLE_ROLES: FamilyRole[] = ['母亲', '父亲', '闺蜜', '兄弟', '七大姑', '八大姨'];

const FamilyMemberCard: React.FC<{
  member: FamilyMemberInput;
  onRemove: () => void;
  onChange: (m: FamilyMemberInput) => void;
}> = ({ member, onRemove, onChange }) => {
  const defaults = FAMILY_ROLE_DEFAULTS.find(d => d.role === member.role);

  return (
    <div className="bg-white rounded-xl p-3 border border-[#E5E0D8] relative">
      <button
        onClick={onRemove}
        className="absolute top-2 right-2 w-5 h-5 flex items-center justify-center rounded-full bg-gray-100 hover:bg-red-100 text-gray-400 hover:text-red-500 transition-colors"
      >
        <X size={12} />
      </button>

      <div className="flex items-center gap-2 mb-2">
        <span className="text-lg">{defaults?.emoji}</span>
        <span className="font-bold text-sm">{member.role}</span>
        <span className="text-xs text-[#9CA3AF]">{defaults?.description}</span>
      </div>

      <div className="flex gap-2">
        <input
          type="text"
          placeholder={`性格（默认：${defaults?.defaultPersonality}）`}
          value={member.personality ?? ''}
          onChange={e => onChange({ ...member, personality: e.target.value || undefined })}
          className="flex-1 px-2 py-1.5 rounded-lg border border-[#E5E0D8] text-xs focus:outline-none focus:border-coral-400"
        />
        <input
          type="text"
          placeholder={`职业（默认：${defaults?.defaultJob}）`}
          value={member.job ?? ''}
          onChange={e => onChange({ ...member, job: e.target.value || undefined })}
          className="flex-1 px-2 py-1.5 rounded-lg border border-[#E5E0D8] text-xs focus:outline-none focus:border-coral-400"
        />
      </div>
    </div>
  );
};

const FamilySide: React.FC<FamilySideProps> = ({ label, protagonist, members, onChange }) => {
  const usedRoles = members.map(m => m.role);
  const availableRoles = AVAILABLE_ROLES.filter(r => !usedRoles.includes(r));

  const addMember = (role: FamilyRole) => {
    if (members.length >= 2) return;
    onChange([...members, { role }]);
  };

  const removeMember = (index: number) => {
    onChange(members.filter((_, i) => i !== index));
  };

  const updateMember = (index: number, m: FamilyMemberInput) => {
    const updated = [...members];
    updated[index] = m;
    onChange(updated);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <span className="font-bold text-sm">{label}</span>
        <span className="text-xs text-[#9CA3AF]">
          {protagonist.name}的亲友团（{members.length}/2）
        </span>
      </div>

      {members.map((m, i) => (
        <FamilyMemberCard
          key={`${m.role}-${i}`}
          member={m}
          onRemove={() => removeMember(i)}
          onChange={(updated) => updateMember(i, updated)}
        />
      ))}

      {members.length < 2 && (
        <div className="flex flex-wrap gap-1.5">
          {availableRoles.map(role => {
            const defaults = FAMILY_ROLE_DEFAULTS.find(d => d.role === role);
            return (
              <button
                key={role}
                onClick={() => addMember(role)}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-gray-50 border border-dashed border-[#E5E0D8] text-xs text-[#6B7280] hover:border-coral-400 hover:text-coral-500 transition-colors"
              >
                <Plus size={10} />
                {defaults?.emoji} {role}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const FamilySelectScreen: React.FC<FamilySelectScreenProps> = ({
  protagonistA,
  protagonistB,
  onComplete,
  isGenerating,
}) => {
  const [familyA, setFamilyA] = useState<FamilyMemberInput[]>([{ role: '母亲' }]);
  const [familyB, setFamilyB] = useState<FamilyMemberInput[]>([{ role: '父亲' }]);

  const randomize = useCallback(() => {
    const pickRandom = (): FamilyMemberInput[] => {
      const shuffled = [...AVAILABLE_ROLES].sort(() => Math.random() - 0.5);
      const count = 1 + Math.floor(Math.random() * 2);
      return shuffled.slice(0, count).map(role => ({ role }));
    };
    setFamilyA(pickRandom());
    setFamilyB(pickRandom());
  }, []);

  const isValid = familyA.length >= 1 && familyB.length >= 1;

  return (
    <div className="min-h-screen bg-gradient-to-b from-cream to-coral-100 p-4 pb-24">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-4 pt-2">
          <h2 className="text-xl font-bold text-[#2D2D2D]">组建亲友团</h2>
          <p className="text-xs text-[#9CA3AF] mt-1">每方选择1-2位亲友参加第一幕的亲友初筛</p>
        </div>

        {/* 一键随机 */}
        <button
          onClick={randomize}
          className="w-full mb-4 py-2 rounded-xl bg-white border border-dashed border-[#E5E0D8] text-sm text-[#6B7280] hover:border-coral-400 hover:text-coral-500 transition-colors flex items-center justify-center gap-2"
        >
          <Shuffle size={14} />
          随机组建亲友团
        </button>

        {/* 双方亲友 */}
        <div className="space-y-6">
          <FamilySide
            label="男方亲友"
            protagonist={protagonistA}
            members={familyA}
            onChange={setFamilyA}
          />
          <div className="border-t border-[#E5E0D8]" />
          <FamilySide
            label="女方亲友"
            protagonist={protagonistB}
            members={familyB}
            onChange={setFamilyB}
          />
        </div>
      </div>

      {/* 底部按钮 */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-cream via-cream to-transparent">
        <div className="max-w-lg mx-auto">
          <button
            onClick={() => isValid && onComplete(familyA, familyB)}
            disabled={!isValid || isGenerating}
            className={`w-full py-3.5 rounded-full font-bold text-base flex items-center justify-center gap-2 transition-all ${
              isValid && !isGenerating
                ? 'bg-gradient-to-r from-coral-500 to-amber-accent text-white shadow-lg hover:shadow-xl'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {isGenerating ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                AI 正在生成亲友...
              </>
            ) : (
              <>
                开始相亲！
                <ChevronRight size={16} />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FamilySelectScreen;
